import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Subject, Observable, merge, fromEvent, from, Subscription } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { get } from 'lodash-es';
import { JwtHelperService } from '@auth0/angular-jwt';

import { User } from '@model/authentication.model';

const jwtHelperService: JwtHelperService = new JwtHelperService();

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  currentUser: User;
  private currentUserSubject$: Subject<User> = new Subject<User>();
  currentUser$: Observable<User> = this.currentUserSubject$.asObservable();

  private debouncedUserState$: Subject<string> = new Subject<string>();
  private userStateSubscription: Subscription;
  private idleTime: number = 60000 * 30;

  constructor(private translate: TranslateService) {}

  sendUserMessage(user: User): Promise<void> {
    return new Promise(
      (resolve: any, reject: any): void => {
        try {
          this.currentUser = user;
          this.currentUserSubject$.next(user);

          if (user) {
            resolve();
          } else {
            reject();
          }
        } catch (error) {
          reject();
        }
      }
    );
  }

  getUserType(): string {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.userType;
  }

  getUserBranchCode(): string {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.branchCode;
  }

  getUserIMDCode(): string {
    if (!this.currentUser) {
      return null;
    }
    return this.currentUser.intermediaryId;
  }

  clearCurrentUser(): void {
    this.currentUser = null;
  }

  getHttpHeader(contentType: string = 'application/json'): HttpHeaders {
    if (!this.currentUser) {
      return null;
    }

    const commonHeader: any = {
      'Accept-Language': this.translate.currentLang,
      Authorization: `Bearer ${get(this.currentUser, 'accessToken.access_token', '')}`
    };

    if (contentType) {
      return new HttpHeaders({
        ...commonHeader,
        'Content-Type': contentType
      });
    }
    return new HttpHeaders(commonHeader);
  }

  checkExpiredToken(): void {
    if (!this.currentUser || !this.currentUser.accessToken || !this.currentUser.accessToken.access_token) {
      return;
    }

    if (jwtHelperService.isTokenExpired(this.currentUser.accessToken.access_token)) {
      window.parent.postMessage([{ channel: 'Unauthorized' }], '*');
    }
  }

  getUserState(): Observable<string> {
    return this.debouncedUserState$.asObservable();
  }

  refreshUserState(): any {
    this.debouncedUserState$.next('logout');
  }

  manageActiveUserState(): void {
    this.userStateSubscription = this.getUserState()
      .pipe(debounceTime(this.idleTime))
      .subscribe(() => {
        this.userStateSubscription.unsubscribe();
        window.parent.postMessage([{ channel: 'Unauthorized' }], '*');
      });
  }

  startWatching(): void {
    this.manageActiveUserState();
    this.refreshUserState();

    from(merge(fromEvent(window, 'mousemove'), fromEvent(window, 'resize'), fromEvent(document, 'keydown'))).subscribe(() => {
      this.refreshUserState();
    });
  }
}
